import React, { useEffect, useState } from 'react';
import { fetchDashboardDrilldown, fetchLeadTimeline } from '../../services/api';
import { TimelineEntry } from '../../types';

const fmt     = (n: number) => `₹${Number(n).toLocaleString('en-IN')}`;
const fmtDate = (s?: string | null) =>
  s ? new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
const fmtDay  = (s?: string | null) =>
  s ? new Date(s).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : '—';
const fmtDur  = (sec?: number) => {
  if (!sec) return '—';
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${m}m ${s}s`;
};

interface DrillData {
  type: 'leads' | 'deals' | 'calls' | 'tasks';
  title: string;
  count: number;
  rows: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
}

interface Props {
  metric: string;
  label: string;
  params: { date_from?: string; date_to?: string; agent_id?: string };
  onClose: () => void;
}

type LeadRef = { id: number; name: string };

const DrilldownModal: React.FC<Props> = ({ metric, label, params, onClose }) => {
  const [data, setData]       = useState<DrillData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [timelineLead, setTimelineLead] = useState<LeadRef | null>(null);

  useEffect(() => {
    setLoading(true); setError(null); setTimelineLead(null);
    fetchDashboardDrilldown({ metric, ...params })
      .then(setData)
      .catch(() => setError('Failed to load records'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metric, params.date_from, params.date_to, params.agent_id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { timelineLead ? setTimelineLead(null) : onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, timelineLead]);

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <div style={header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {timelineLead && (
              <button onClick={() => setTimelineLead(null)} style={backBtn} aria-label="Back to list">←</button>
            )}
            <div>
              <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 16 }}>
                {timelineLead ? `Timeline — ${timelineLead.name || 'Lead'}` : label}
              </div>
              {!timelineLead && data && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {data.count} record{data.count === 1 ? '' : 's'}
                  {data.count >= 300 ? ' · showing first 300' : ''}
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} style={closeBtn} aria-label="Close">×</button>
        </div>

        <div style={body}>
          {timelineLead ? (
            <LeadTimelinePanel leadId={timelineLead.id} />
          ) : (
            <>
              {loading && <div className="spinner">Loading&hellip;</div>}
              {error && <div className="alert alert-error">{error}</div>}
              {data && !loading && (data.rows.length === 0
                ? <div style={{ padding: 24, color: 'var(--text-muted)', textAlign: 'center' }}>No records for this period.</div>
                : renderTable(data, setTimelineLead))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

function renderTable(data: DrillData, onLeadTimeline: (l: LeadRef) => void) {
  const rows = data.rows;
  switch (data.type) {
    case 'leads':
      return (
        <table style={table}>
          <thead><tr><Th>Name</Th><Th>Mobile</Th><Th>Source</Th><Th>Status</Th><Th>Assigned</Th><Th>Action</Th></tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.id} style={tr}>
              <Td bold>{r.name || '—'}</Td>
              <Td>{r.mobile || '—'}</Td>
              <Td muted>{r.source || '—'}</Td>
              <Td><span className={`badge badge-${r.status}`}>{r.deal_stage || r.lead_status || r.status || '—'}</span></Td>
              <Td muted>{r.assigned || 'Unassigned'}</Td>
              <Td>
                <button style={timelineBtn} onClick={() => onLeadTimeline({ id: r.id, name: r.name })}>
                  Timeline
                </button>
              </Td>
            </tr>
          ))}</tbody>
        </table>
      );
    case 'deals':
      return (
        <table style={table}>
          <thead><tr><Th>Title</Th><Th>Value</Th><Th>Stage</Th><Th>Prob.</Th><Th>Customer</Th></tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.id} style={tr}>
              <Td bold>{r.title || '—'}</Td>
              <Td><span style={{ color: 'var(--teal)', fontWeight: 600 }}>{fmt(Number(r.value))}</span></Td>
              <Td><span className={`badge stage-${r.stage}`}>{String(r.stage).replace('_', ' ')}</span></Td>
              <Td muted>{r.probability != null ? `${r.probability}%` : '—'}</Td>
              <Td muted>{r.customer || '—'}</Td>
            </tr>
          ))}</tbody>
        </table>
      );
    case 'calls':
      return (
        <table style={table}>
          <thead><tr><Th>Number</Th><Th>Duration</Th><Th>Status</Th><Th>Agent</Th><Th>Time</Th></tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.id} style={tr}>
              <Td bold>{r.called_phone || '—'}</Td>
              <Td>{fmtDur(r.duration_seconds)}</Td>
              <Td muted>{r.call_status || '—'}</Td>
              <Td muted>{r.agent || '—'}</Td>
              <Td muted>{fmtDate(r.started_at)}</Td>
            </tr>
          ))}</tbody>
        </table>
      );
    case 'tasks':
      return (
        <table style={table}>
          <thead><tr><Th>Task</Th><Th>Due</Th><Th>Type</Th><Th>Related</Th><Th>Assigned</Th></tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.id} style={tr}>
              <Td bold>{r.title || '—'}</Td>
              <Td>{fmtDay(r.due_date)}</Td>
              <Td muted>{r.is_auto ? 'Auto' : 'Manual'}</Td>
              <Td muted>{r.related || '—'}</Td>
              <Td muted>{r.assigned || '—'}</Td>
            </tr>
          ))}</tbody>
        </table>
      );
    default:
      return null;
  }
}

// ── Lead timeline sub-view ──────────────────────────────────────────────────
const entryIcon = (e: TimelineEntry): string => {
  if (e.kind === 'interaction') {
    if (e.channel === 'voice_ai')    return '🤖';
    if (e.channel === 'voice_human') return '📱';
    if (e.channel === 'whatsapp')    return '💬';
    if (e.channel === 'chat')        return '💭';
    return '↔️';
  }
  return ({ activity: '📋', note: '📝', task: '✅', call: '📞' } as Record<string, string>)[e.kind] || '•';
};

const LeadTimelinePanel: React.FC<{ leadId: number }> = ({ leadId }) => {
  const [entries, setEntries] = useState<TimelineEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    setLoading(true); setError(null);
    fetchLeadTimeline(leadId)
      .then(setEntries)
      .catch(() => setError('Failed to load timeline'))
      .finally(() => setLoading(false));
  }, [leadId]);

  if (loading) return <div className="spinner">Loading&hellip;</div>;
  if (error)   return <div className="alert alert-error">{error}</div>;
  if (!entries || entries.length === 0)
    return <div style={{ padding: 24, color: 'var(--text-muted)', textAlign: 'center' }}>No timeline activity for this lead.</div>;

  return (
    <div style={{ padding: '14px 20px' }}>
      {entries.map((e, i) => (
        <div key={`${e.kind}-${e.id}-${i}`} style={{ display: 'flex', gap: 12, paddingBottom: 14 }}>
          <div style={{ fontSize: 18, width: 26, textAlign: 'center', flexShrink: 0 }}>{entryIcon(e)}</div>
          <div style={{ flex: 1, borderBottom: '1px solid var(--border)', paddingBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>
              {e.title || e.summary || e.source || e.kind}
            </div>
            {e.summary && e.title && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{e.summary}</div>
            )}
            {e.transcript_text && !e.summary && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, whiteSpace: 'pre-wrap' }}>
                {e.transcript_text.slice(0, 400)}{e.transcript_text.length > 400 ? '…' : ''}
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              {e.channel ? `${e.channel} · ` : ''}{e.direction ? `${e.direction} · ` : ''}{fmtDate(e.timestamp)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: '#fff' }}>{children}</th>
);
const Td: React.FC<{ children: React.ReactNode; bold?: boolean; muted?: boolean }> = ({ children, bold, muted }) => (
  <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: bold ? 500 : 400, color: muted ? 'var(--text-muted)' : 'var(--navy)' }}>{children}</td>
);

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 };
const modal: React.CSSProperties = { background: '#fff', borderRadius: 12, width: 'min(880px, 100%)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' };
const header: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border)' };
const body: React.CSSProperties = { overflow: 'auto' };
const table: React.CSSProperties = { width: '100%', borderCollapse: 'collapse' };
const tr: React.CSSProperties = { borderBottom: '1px solid var(--border)' };
const closeBtn: React.CSSProperties = { background: 'none', border: 'none', fontSize: 26, lineHeight: 1, cursor: 'pointer', color: 'var(--text-muted)' };
const backBtn: React.CSSProperties = { background: 'none', border: '1px solid var(--border)', borderRadius: 8, width: 30, height: 30, fontSize: 16, lineHeight: 1, cursor: 'pointer', color: 'var(--navy)', flexShrink: 0 };
const timelineBtn: React.CSSProperties = { background: 'var(--primary, #534AB7)', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };

export default DrilldownModal;
